import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { apiService, extractApiErrorMessage } from '../../services/api';
import { buildVehicleSearchEndpoint } from '../../services/collectionEndpoints';
import StableDropdown from '../ui/StableDropdown';

interface VehicleOption {
  value: string;
  text: string;
  subtitle?: string;
  data?: unknown;
}

interface LinkVehicleCardProps {
  dealId: string;
  /** Called after the deal is successfully linked so the parent can refetch. */
  onLinked: () => void;
}

/**
 * Shown on a BHPH deal that has no `vehicle_id`. Lets the user attach an
 * inventory vehicle so GPS tracking (which is per-vehicle) can be enabled.
 * The backend `PUT /deals/{id}` already accepts `vehicle_id`.
 */
const LinkVehicleCard: React.FC<LinkVehicleCardProps> = ({ dealId, onLinked }) => {
  const [options, setOptions] = useState<VehicleOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);

  const searchVehicles = async (query: string): Promise<VehicleOption[]> => {
    if (!query.trim()) return [];
    setLoading(true);
    try {
      const response = await apiService.get<Record<string, unknown>>(
        buildVehicleSearchEndpoint(query)
      );
      // Endpoint may return a bare array or an object wrapping `data`/`vehicles`.
      const raw = Array.isArray(response)
        ? response
        : ((response.data || response.vehicles || []) as Record<string, unknown>[]);
      const vehicles = Array.isArray(raw) ? raw : [];
      const opts: VehicleOption[] = vehicles.map((v) => ({
        value: (v.id as string)?.toString() || '',
        text: `${v.year || ''} ${v.make || ''} ${v.model || ''}`.trim() || 'Unknown Vehicle',
        subtitle: `$${(v.asking_price as number)?.toLocaleString() || '0'} • Stock: ${
          (v.stock_number as string) || 'N/A'
        }`,
        data: v,
      }));
      setOptions(opts);
      return opts;
    } catch (err) {
      console.error('Error searching vehicles:', err);
      toast.error('Failed to search inventory');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (value: string, option: VehicleOption) => {
    if (!value || linking) return;
    // A deal must share its vehicle's location anchor, so the deal is
    // re-anchored to the selected vehicle's location_id on link. Without it
    // the backend rejects the update when the deal's old location differs.
    const locationId = (option.data as { location_id?: string } | undefined)?.location_id;
    if (!locationId) {
      toast.error('Selected vehicle has no location — cannot link');
      return;
    }
    setLinking(true);
    try {
      await apiService.put(`/deals/${dealId}`, {
        vehicle_id: value,
        location_id: locationId,
      });
      toast.success('Vehicle linked — GPS tracking is now available');
      onLinked();
    } catch (err) {
      toast.error(extractApiErrorMessage(err, 'Failed to link vehicle'));
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="rounded-lg border border-dashed border-gray-300 p-4">
      <h3 className="text-lg font-medium text-gray-900 mb-1">GPS Tracking Device</h3>
      <p className="text-sm text-gray-600 mb-3">
        Link an inventory vehicle to this deal to enable GPS tracking.
      </p>
      <StableDropdown
        options={options}
        onSelect={handleSelect}
        onSearch={searchVehicles}
        placeholder="Type to search vehicle by year, make, model, or stock..."
        loading={loading}
        disabled={linking}
        hideChevron
        className="w-full"
      />
    </div>
  );
};

export default LinkVehicleCard;
